import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { countXLRConnectors, getFinishingPricingSync, getHeatShrinkEndPrice } from '../services/pricingService';
import { compatibilityMatrix } from '../services/compatibilityMatrix';
import CustomHeatShrinkPanel from './CustomHeatShrinkPanel';
import { countCustomHeatShrinkEnds } from '../utils/customHeatShrinkHelpers';

const finishingOptions = {
  heatShrink: {
    label: 'Heat Shrink',
    options: [
      { value: 'none', label: 'No Additional' },
      { value: 'clear', label: '2" clear (plain, unshrunk)' },
      { value: 'custom', label: '2" custom printed' },
      { value: 'clear+custom', label: '2" clear + 2" custom printed' }
    ],
    price: 1.00 // Price per end
  }
};

// Coding ring color options
const codingRingColors = [
  { value: 'none', label: 'Black is standard' },
  { value: 'brown', label: 'Brown' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
  { value: 'violet', label: 'Violet' },
  { value: 'grey', label: 'Grey' },
  { value: 'white', label: 'White' }
];

function normalizeFinishing(selectedFinishing) {
  // Ensure each finishing option is always an object with a 'type' property
  const normalized = {};
  for (const key of Object.keys(finishingOptions)) {
    const val = selectedFinishing[key];
    if (typeof val === 'string') {
      normalized[key] = { type: val };
    } else if (typeof val === 'object' && val !== null) {
      normalized[key] = { ...val };
    } else {
      normalized[key] = { type: 'none' };
    }
  }
  
  // Handle heatShrink - support both old format (single type) and new format (per-connector)
  if (selectedFinishing.heatShrink) {
    if (typeof selectedFinishing.heatShrink === 'string') {
      // Old format - convert to new format
      normalized.heatShrink = { global: selectedFinishing.heatShrink };
    } else if (typeof selectedFinishing.heatShrink === 'object' && selectedFinishing.heatShrink.type) {
      // Old format with type property - convert to new format
      normalized.heatShrink = { global: selectedFinishing.heatShrink.type };
    } else {
      // New format - per-connector heat shrink
      normalized.heatShrink = { ...selectedFinishing.heatShrink };
    }
  } else {
    normalized.heatShrink = { global: 'none' };
  }
  
  // Handle codingRings - support both old format (single color) and new format (per-connector)
  if (selectedFinishing.codingRings) {
    if (typeof selectedFinishing.codingRings === 'string') {
      // Old format - convert to new format
      normalized.codingRings = { global: selectedFinishing.codingRings };
    } else {
      // New format - per-connector colors
      normalized.codingRings = { ...selectedFinishing.codingRings };
    }
  } else {
    normalized.codingRings = { global: 'none' };
  }
  
  // Handle cableColor - for hybrid cables only
  if (selectedFinishing.cableColor) {
    normalized.cableColor = selectedFinishing.cableColor;
  } else {
    normalized.cableColor = 'black'; // Default to black
  }

  if (selectedFinishing.customHeatShrink) {
    normalized.customHeatShrink = { ...selectedFinishing.customHeatShrink };
  }
  
  return normalized;
}

// Helper function to identify XLR connectors
const isXLRConnector = (conn) => {
  if (!conn) return false;
  
  return (
    (conn.connector && (
      conn.connector.includes('XLR') ||
      conn.connector.includes('XL') ||
      conn.connector.includes('NC3') ||
      conn.connector.includes('NC5')
    )) ||
    (conn.skuCode && (
      conn.skuCode.includes('XLR') ||
      conn.skuCode.includes('XL') ||
      conn.skuCode.includes('NC3') ||
      conn.skuCode.includes('NC5')
    )) ||
    (conn.id && (
      conn.id.includes('XLR') ||
      conn.id.includes('XL') ||
      conn.id.includes('NC3') ||
      conn.id.includes('NC5')
    )) ||
    (conn.family === 'XLR')
  );
};

// Helper function to get connector display name
const getConnectorDisplayName = (conn, position) => {
  if (!conn) return position;
  
  let name = 'Unknown connector';
  
  if (conn.id) {
    // Get the proper description from the compatibility matrix
    const matrixEntry = compatibilityMatrix[conn.id];
    if (matrixEntry?.description) {
      name = matrixEntry.description;
    } else if (matrixEntry?.name) {
      name = matrixEntry.name;
    } else if (conn.description) {
      name = conn.description;
    } else if (conn.label) {
      name = conn.label;
    } else if (conn.name) {
      name = conn.name;
    } else {
      name = conn.id;
    }
  } else {
    // Fallback for connectors without ID
    name = conn.name || conn.description || conn.label || conn.id || conn.skuCode || conn.connector || 'Unknown connector';
  }
  
  return `${position}: ${name}`;
};

// Helper function to get all XLR connectors from config
const getXLRConnectors = (config) => {
  const xlrConnectors = [];
  
  // Check standard connectors
  if (config.connectors?.endA && isXLRConnector(config.connectors.endA)) {
    xlrConnectors.push({
      key: 'endA',
      position: 'End A',
      connector: config.connectors.endA
    });
  }
  
  if (config.connectors?.endB && isXLRConnector(config.connectors.endB)) {
    xlrConnectors.push({
      key: 'endB',
      position: 'End B',
      connector: config.connectors.endB
    });
  }
  
  // Check hybrid connectors
  if (config.hybridConnectors?.connectors) {
    Object.entries(config.hybridConnectors.connectors).forEach(([key, connector]) => {
      if (connector && isXLRConnector(connector)) {
        xlrConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  // Check wye connectors
  if (config.wyeConnectors?.connectors) {
    Object.entries(config.wyeConnectors.connectors).forEach(([key, connector]) => {
      if (connector && isXLRConnector(connector)) {
        xlrConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  // Check zip connectors
  if (config.zipConnectors?.connectors) {
    Object.entries(config.zipConnectors.connectors).forEach(([key, connector]) => {
      if (connector && isXLRConnector(connector)) {
        xlrConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  return xlrConnectors;
};

function FinishingSelection({ onChange, value, config = {}, sku = '' }) {
  // Feature flag for color choice - can be controlled via props or environment
  const offerColorChoice = config.offerColorChoice ?? process.env.REACT_APP_OFFER_COLOR_CHOICE === 'true' ?? false;
  
  // Initialize finishing with cable color from hybrid connectors if available
  const initialFinishing = value || {};
  
  // If cable color is not set in finishing but is available in hybrid connectors, use it
  if (offerColorChoice && !initialFinishing.cableColor && config.hybridConnectors?.cableColor) {
    initialFinishing.cableColor = config.hybridConnectors.cableColor;
  }
  
  const finishing = normalizeFinishing(initialFinishing);
  const xlrConnectors = getXLRConnectors(config);
  
  // Get all connectors for heat shrink options
  const allConnectors = [];
  
  // Check if this is a hybrid/zip/wye cable
  const isHybridCable = config.style && ['hybrid', 'hybrid-power-audio', 'hybrid-power-audio-audio', 'zip', 'zip-audio-audio', 'wye'].includes(config.style);
  
  // Only add standard connectors (endA/endB) if this is NOT a hybrid/zip/wye cable
  if (!isHybridCable) {
    if (config.connectors?.endA) {
      allConnectors.push({
        key: 'endA',
        position: 'End A',
        connector: config.connectors.endA
      });
    }
    if (config.connectors?.endB) {
      allConnectors.push({
        key: 'endB',
        position: 'End B',
        connector: config.connectors.endB
      });
    }
  }
  
  // Add hybrid connectors
  if (config.hybridConnectors?.connectors) {
    Object.entries(config.hybridConnectors.connectors).forEach(([key, connector]) => {
      if (connector) {
        allConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  // Add wye connectors
  if (config.wyeConnectors?.connectors) {
    Object.entries(config.wyeConnectors.connectors).forEach(([key, connector]) => {
      if (connector) {
        allConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  // Add zip connectors
  if (config.zipConnectors?.connectors) {
    Object.entries(config.zipConnectors.connectors).forEach(([key, connector]) => {
      if (connector) {
        allConnectors.push({
          key: key,
          position: `Connector ${key}`,
          connector: connector
        });
      }
    });
  }
  
  // Calculate total heat shrink cost
  let heatShrinkTotal = 0;
  allConnectors.forEach(({ key }) => {
    const heatShrinkType = finishing.heatShrink[key] || finishing.heatShrink.global || 'none';
    heatShrinkTotal += getHeatShrinkEndPrice(heatShrinkType);
  });
  
  // Calculate total coding rings cost
  let codingRingsTotal = 0;
  xlrConnectors.forEach(({ key }) => {
    const color = finishing.codingRings[key] || finishing.codingRings.global || 'none';
    const unitPrice = getFinishingPricingSync('codingRings', color);
    codingRingsTotal += unitPrice;
  });

  const handleHeatShrinkChange = (connectorKey, heatShrinkType) => {
    const newHeatShrink = { ...finishing.heatShrink };
    newHeatShrink[connectorKey] = heatShrinkType;
    const anyCustom = Object.entries(newHeatShrink).some(
      ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
    );

    onChange({
      ...finishing,
      heatShrink: newHeatShrink,
      ...(anyCustom ? {} : { customHeatShrink: undefined })
    });
  };

  const handleCodingRingChange = (connectorKey, color) => {
    const newCodingRings = { ...finishing.codingRings };
    newCodingRings[connectorKey] = color;
    
    onChange({
      ...finishing,
      codingRings: newCodingRings
    });
  };

  const handleCableColorChange = (color) => {
    onChange({
      ...finishing,
      cableColor: color
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Choose Finishing Options
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        NLFX branding stays at each connector; optional add-ons below sit farther up the cable on the jacket.
      </Typography>

      <Grid container spacing={3}>
        {/* Heat Shrink Option */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Heat Shrink
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tubing sits on the jacket above the plug—choose per end. <i>(${heatShrinkTotal.toFixed(2)} total)</i>
                <br />
                <i>
                  Clear: unshrunk tube, no printing. Custom printed: text or logo; tube and text colors are set in
                  the panel. You can pick clear + custom on one end (both prices apply).
                </i>
              </Typography>
              
              {allConnectors.map(({ key, position, connector }, index) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {getConnectorDisplayName(connector, position)}
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Heat Shrink</InputLabel>
                    <Select
                      value={finishing.heatShrink[key] || finishing.heatShrink.global || 'none'}
                      onChange={(e) => handleHeatShrinkChange(key, e.target.value)}
                      label="Heat Shrink"
                    >
                      {finishingOptions.heatShrink.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {index < allConnectors.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}

              {Object.values(finishing.heatShrink || {}).some((v) => v === 'clear' || v === 'clear+custom') && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Clear add-on is supplied unshrunk—you can add labels or shrink it yourself.
                  </Typography>
                </Box>
              )}

              {Object.entries(finishing.heatShrink || {}).some(
                ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
              ) && (
                <CustomHeatShrinkPanel
                  value={finishing.customHeatShrink}
                  onChange={(ch) => onChange({ ...finishing, customHeatShrink: ch })}
                  sku={sku}
                  customEndCount={countCustomHeatShrinkEnds({ heatShrink: finishing.heatShrink }, config)}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* XLR Coding Rings Option - Only show if XLR connectors are present */}
        {xlrConnectors.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  XLR Coding Rings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose coding ring colors for each XLR connector. <i>(${codingRingsTotal.toFixed(2)} total)</i>
                </Typography>
                
                {xlrConnectors.map(({ key, position, connector }, index) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getConnectorDisplayName(connector, position)}
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Coding Ring Color</InputLabel>
                      <Select
                        value={finishing.codingRings[key] || finishing.codingRings.global || 'none'}
                        onChange={(e) => handleCodingRingChange(key, e.target.value)}
                        label="Coding Ring Color"
                      >
                        {codingRingColors.map((color) => (
                          <MenuItem key={color.value} value={color.value}>
                            {color.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {index < xlrConnectors.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Cable Color Option - Only show for hybrid cables and when color choice is offered */}
        {isHybridCable && offerColorChoice && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cable Color
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose the color for your hybrid cable.
                </Typography>
                
                <FormControl fullWidth>
                  <InputLabel>Cable Color</InputLabel>
                  <Select
                    value={finishing.cableColor || 'black'}
                    onChange={(e) => handleCableColorChange(e.target.value)}
                    label="Cable Color"
                  >
                    <MenuItem value="black">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'black', borderRadius: 1, border: '1px solid #ccc' }} />
                        Black
                      </Box>
                    </MenuItem>
                    <MenuItem value="white">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'white', borderRadius: 1, border: '1px solid #ccc' }} />
                        White
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default FinishingSelection; 